import { FC, useEffect } from 'react';
import Page from 'src/shared-components/Page/Page';
import { FormattedMessage } from 'react-intl';
import {
  generateCatalogPageLink,
  generateFirstLevelCategoryPageLink,
  generateSecondLevelCategoryPageLink,
  generateThirdLevelCategoryPageLink
} from 'src/utils/links';
import NextImage from 'src/shared-components/NextImage/NextImage';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import { FirstCategoryLevel } from 'src/api/types';
import dynamic from 'next/dynamic';
import Skeleton from 'react-loading-skeleton';
import { delayWrapper } from 'src/utils/delayWrapper';
import trns from './trns';

const BreadCrumbs = dynamic(
  () =>
    delayWrapper(
      import('src/shared-components/design-system/BreadCrumbs').then(
        (file) => file.BreadCrumbs
      )
    ),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

const NavigationLink = dynamic(
  () =>
    delayWrapper<typeof NavigationLink>(
      import('src/shared-components/NavigationLink/NavigationLink')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

const Constraints = dynamic(
  () =>
    delayWrapper<typeof Constraints>(
      import('src/shared-components/Constraints/Constraints')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={600} />
  }
);

interface Props {
  gtagPrefix?: GTAGPrefix;
  multyLevelCategoriesList: FirstCategoryLevel[];
}

const CatalogContainer: FC<Props> = ({
  gtagPrefix,
  multyLevelCategoriesList
}) => {
  const { trackGtagEvent } = useGTAG();

  useEffect(() => {
    trackGtagEvent(`${gtagPrefix}_open`);
  }, []);

  return (
    <Page>
      <BreadCrumbs
        items={[
          {
            id: 'home',
            title: <FormattedMessage {...trns.homeBreadCrumb} />,
            href: '/'
          },
          {
            id: 'catalog',
            title: <FormattedMessage {...trns.catalogBreadCrumb} />,
            href: generateCatalogPageLink()
          }
        ]}
      />
      <Constraints className="pb-12 w-[90%]">
        <h1 className="text-black fs-header5-bold pt-[17px] lm:pt-[25px] pr-4">
          <FormattedMessage {...trns.catalogBreadCrumb} />
        </h1>
        <div className="flex-col space-y-4 mt-[40px] w-full">
          {multyLevelCategoriesList.map((categoryFirstLevel) => (
            <div
              key={categoryFirstLevel.name}
              className="flex-col w-full p-[24px] bg-wildSand border border-alto rounded-xl"
            >
              <div className="flex items-center w-full ">
                <div className="w-[32px] h-[32px] relative">
                  <NextImage
                    alt={categoryFirstLevel?.translitName}
                    src={`category/${categoryFirstLevel?.translitName}.svg`}
                    layout="fixed"
                    width={80}
                    height={80}
                    priority
                    quality={100}
                    className="scale-75 top-0 left-0 -translate-y-1/3 -translate-x-1/4"
                  />
                </div>
                <NavigationLink
                  key={categoryFirstLevel.name}
                  href={generateFirstLevelCategoryPageLink({
                    category: categoryFirstLevel
                  })}
                  passClassName="fs-title3 pl-[15px] z-10 hover:underline hover:text-bahamaBlue"
                >
                  {categoryFirstLevel.name}
                </NavigationLink>
              </div>
              <div className={'grid gap-x-[24px] grid-cols-1'}>
                {categoryFirstLevel?.subCategories?.map(
                  (categorySecondLevel) => (
                    <>
                      <NavigationLink
                        key={categorySecondLevel.name}
                        href={generateSecondLevelCategoryPageLink({
                          subCategoryId: categorySecondLevel?.translitName,
                          category: categoryFirstLevel
                        })}
                        passClassName="fs-title3 my-6 hover:underline hover:text-bahamaBlue"
                      >
                        {categorySecondLevel.name}
                      </NavigationLink>
                      <div className="grid grid-cols-1 m:grid-cols-3 l:grid-cols-4 gap-x-20">
                        {categorySecondLevel?.subCategories?.map(
                          (categoryThirdLevel) => (
                            <div key={categoryThirdLevel.name} className="mb-4">
                              <NavigationLink
                                key={categoryThirdLevel.name}
                                href={generateThirdLevelCategoryPageLink({
                                  category: categoryFirstLevel,
                                  secondCategoryId:
                                    categorySecondLevel?.translitName,
                                  thirdCategoryId:
                                    categoryThirdLevel?.translitName
                                })}
                                passClassName="fs-body-reg hover:underline hover:text-bahamaBlue"
                              >
                                {categoryThirdLevel.name}
                              </NavigationLink>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </Constraints>
    </Page>
  );
};

export default CatalogContainer;
